/**
 * EsoTimeClock.jsx — Dual-Console Modernization (Ja-Kha'jay Restoration).
 *
 * In-game time clock: twin Masser/Secunda moons with authentic astronomical
 * SVG terminator discs, high-contrast plain Tamrielic phase labels (>=14px),
 * directional waxing/waning arrows, and canonical Khajiiti furstock flavor line.
 *
 * Grounded in architecture review by Opus 4.6 & Sonnet 4.6 (createMemo() isolation)
 * and the celestial Ja-Kha'jay vision of Claude Fable 5.1.
 */
import { createMemo, createSignal, onMount, onCleanup } from 'solid-js';
import wm from '../lib/wmStore.js';
import { getESOTime } from '../lib/esoTime.js';
import { pickSettingOr } from '../lib/settingsLayers.js';
import Tooltip from '../components/MouseTooltip.jsx';
import './EsoTimeClock.css';

// #332 — Short Fissal trading hints keyed by moon phase
const JODE_TIPS = {
  new:            'Dark skies — quieter markets. Good for bulk listings.',
  waxingCrescent: 'Market stirring. Buyers are cautiously optimistic.',
  firstQuarter:   'First quarter — gear listings move at a steady clip.',
  waxingGibbous:  'Near peak. Stack your traders now before the rush.',
  full:           'Full Jode — peak market energy. List your best gear.',
  waningGibbous:  'Past the peak. Bargain hunters are sweeping the listings.',
  thirdQuarter:   'Mid-tier loot clears well this half.',
  waningCrescent: 'Caravan reset is close. Watch your listing timers.',
};
const JONE_TIPS = {
  new:            'Jone hides — consumable demand softens slightly.',
  waxingCrescent: "Jone's crescent — motifs and style pages stir.",
  firstQuarter:   'Jone at half — a steady omen for crafted gear.',
  waxingGibbous:  "Jone nearly full — Jode's lead makes tonight bright.",
  full:           'Both moons full in lore. A rare, luminous trading window.',
  waningGibbous:  'Jone waning — the market pulse settles.',
  thirdQuarter:   'Jone at third quarter — consistent, if unspectacular.',
  waningCrescent: 'Jone fading. The cycle winds down toward the new moon.',
};

const MOON_LABELS_LOCAL = {
  new: 'New Moon',
  waxingCrescent: 'Waxing Crescent',
  firstQuarter: 'First Quarter',
  waxingGibbous: 'Waxing Gibbous',
  full: 'Full Moon',
  waningGibbous: 'Waning Gibbous',
  thirdQuarter: 'Third Quarter',
  waningCrescent: 'Waning Crescent',
};

// Canonical Khajiiti 16-Furstock Lattice Matrix (Masser x Secunda)
function getFurstockLattice(masserPhase, secundaPhase) {
  const isFull = (p) => p === 'full';
  const isNew = (p) => p === 'new';
  const isWax = (p) => p === 'waxingCrescent' || p === 'firstQuarter' || p === 'waxingGibbous';

  if (isFull(masserPhase) && isFull(secundaPhase)) return "Both moons full — a Mane's sky";
  if (isNew(masserPhase) && isNew(secundaPhase)) return "Both moons dark — the Hidden Moon";

  const mCat = isFull(masserPhase) ? 'full' : isNew(masserPhase) ? 'new' : isWax(masserPhase) ? 'wax' : 'wan';
  const sCat = isFull(secundaPhase) ? 'full' : isNew(secundaPhase) ? 'new' : isWax(secundaPhase) ? 'wax' : 'wan';

  const furstockMap = {
    full: { full: "Senche", wax: "Senche-raht", new: "Pahmar", wan: "Pahmar-raht" },
    wax:  { full: "Cathay", wax: "Cathay-raht", new: "Tojay", wan: "Tojay-raht" },
    new:  { full: "Alfiq",  wax: "Alfiq-raht",  new: "Ohmes", wan: "Ohmes-raht" },
    wan:  { full: "Dagi",   wax: "Dagi-raht",   new: "Suthay", wan: "Suthay-raht" }
  };

  const name = furstockMap[mCat]?.[sCat] || "Cathay-raht";
  return `The celestial lattice favors the ${name}`;
}

// Compute astronomical SVG shadow terminator path over a lit disc of radius r
function computeMoonTerminator(r, fraction, isWaxing) {
  const f = Math.max(0, Math.min(1, fraction));
  const d = r * 2;
  if (f <= 0.02) {
    return `M ${r} 0 A ${r} ${r} 0 1 0 ${r} ${d} A ${r} ${r} 0 1 0 ${r} 0 Z`;
  }
  if (f >= 0.98) {
    return "";
  }
  const rx = Math.max(0.01, r * Math.abs(2 * f - 1));
  if (isWaxing) {
    const sweep = f < 0.5 ? 0 : 1;
    return `M ${r} 0 A ${r} ${r} 0 0 0 ${r} ${d} A ${rx.toFixed(2)} ${r} 0 0 ${sweep} ${r} 0 Z`;
  } else {
    const sweep = f < 0.5 ? 1 : 0;
    return `M ${r} 0 A ${r} ${r} 0 0 1 ${r} ${d} A ${rx.toFixed(2)} ${r} 0 0 ${sweep} ${r} 0 Z`;
  }
}

// Fissal comments keyed by timeOfDay
const FISSAL_LINES = {
  dawn: [
    "The morning star fades over the hillside. Masser sets; the market stirs.",
    "Dawn — dew on the grass, lanterns still burning in Wayrest.",
    "First light in Tamriel. A fine hour to price your wares.",
  ],
  morning: [
    "Midmorning — the trading posts are buzzing. Good energy for a deal.",
    "Jode climbs high. Merchants are sharp this hour.",
    "Sun's up, satchels out. Competition's brisk.",
  ],
  afternoon: [
    "Midday sun over Elden Root. Peak hours for guild traders.",
    "The market is loud and bright. Watch the undercuts.",
    "Afternoon — perfect Roe moves fast right now.",
  ],
  dusk: [
    "Jode dips amber on the horizon. Evening rush incoming.",
    "The torches are lit in Mournhold. Last chance for big sales.",
    "Dusk in Tamriel. Masser will be beautiful tonight.",
  ],
  night: [
    "Quiet hour. Fissal is tinkering by starlight — Masser and Secunda overhead.",
    "The moons watch over the sleeping markets. Rest well, traveler.",
    "Late night in Tamriel. A few night-owls still checking prices.",
  ],
};

const PHASE_LINES = {
  new: [
    "Both moons hide tonight. Good time to list a Nirnroot before the Caravan cycle resets.",
    "New moon — Sheogorath's favorite weather. Watch your prices.",
  ],
  waxingCrescent: [
    "A sliver of Jode peeks out. Trade winds are gathering.",
    "Waxing — buyers feel hopeful. Inflate carefully.",
  ],
  firstQuarter: [
    "First quarter. Markets are warming up — half-light, half-promise.",
    "Jode is half-bright. Listings move at a steady clip.",
  ],
  waxingGibbous: [
    "Almost full — every merchant in Cyrodiil knows it. Stack your traders.",
    "Gibbous Jode. Tomorrow's the day. List your bigs now.",
  ],
  full: [
    "Full moons over Tamriel. Wares glitter, scams glitter brighter — read the asks twice.",
    "Masser at her brightest. Big-ticket gear loves a full-moon listing.",
  ],
  waningGibbous: [
    "Waning gibbous. The peak rush is past — bargain hunters are out.",
    "Jode dimming. Second-tier listings start to clear.",
  ],
  thirdQuarter: [
    "Third quarter — the moons split the sky. Mid-tier loot moves well.",
    "Half Jode, half shadow. Steady trades, fewer impulse buys.",
  ],
  waningCrescent: [
    "Waning crescent. The Caravan reset is close — set your alerts.",
    "Last sliver. Resist the urge to undercut yourself.",
  ],
};

function pickComment(timeOfDay, phase, seed) {
  const phasePool = PHASE_LINES[phase] || [];
  const todPool   = FISSAL_LINES[timeOfDay] || FISSAL_LINES.night;
  const usePhase = (seed % 2 === 0) && phasePool.length > 0;
  const pool = usePhase ? phasePool : todPool;
  return pool[seed % pool.length];
}

const CLOCK_THEME_CLASSES = {
  moonlit: 'is-theme-moonlit',
  dawn: 'is-theme-dawn',
  bloodmoon: 'is-theme-bloodmoon',
  apocrypha: 'is-theme-apocrypha',
};

const TIME_OF_DAY_LABELS = {
  dawn: 'Dawn',
  morning: 'Morning',
  afternoon: 'Afternoon',
  dusk: 'Dusk',
  night: 'Night',
};

function legacyMoonMode() {
  if (typeof localStorage === 'undefined') return 'in-game';
  return localStorage.getItem('redfur-eso-moon-mode') === 'lore' ? 'lore' : 'in-game';
}

export default function EsoTimeClock(props = {}) {
  const [eso, setEso] = createSignal(getESOTime());
  const [hovered, setHovered] = createSignal(false);
  const [commentSeed, setCommentSeed] = createSignal(Math.floor(Date.now() / 60_000));
  const [legacyMode, setLegacyMode] = createSignal(legacyMoonMode());

  const settings = () => props.settings || props.widget?.settings || {};
  const setting = (key, fallback) => pickSettingOr(settings(), key, props.maximized, fallback);
  const enabled = (key, fallback = true) => setting(key, fallback) !== false;

  const moonPhaseMode = createMemo(() => setting('moonPhaseMode', legacyMode()) === 'lore' ? 'lore' : 'in-game');
  const clockTheme = createMemo(() => String(setting('clockTheme', '') || ''));
  const themeClass = createMemo(() => CLOCK_THEME_CLASSES[clockTheme()] || '');
  const showMoons = createMemo(() => enabled('showMoons'));
  const showDate = createMemo(() => enabled('showDate'));
  const showPhase = createMemo(() => enabled('showPhase'));
  const showEarthMoon = createMemo(() => enabled('showEarthMoon'));
  const showLoreToggle = createMemo(() => enabled('showLoreToggle'));
  const showCommentary = createMemo(() => enabled('showCommentary'));
  const animateMoons = createMemo(() => enabled('animateMoons'));

  // Lunar Phase Resolution
  const displayJodePhase = createMemo(() => moonPhaseMode() === 'lore' ? eso().loreJodePhase : eso().jodePhase);
  const displayJonePhase = createMemo(() => moonPhaseMode() === 'lore' ? eso().loreJonePhase : eso().jonePhase);

  const displayJodeIllumination = createMemo(() => moonPhaseMode() === 'lore'
    ? eso().loreJodeIlluminationPercent
    : eso().esoJodeIlluminationPercent);
  const displayJoneIllumination = createMemo(() => moonPhaseMode() === 'lore'
    ? eso().loreJoneIlluminationPercent
    : eso().esoJoneIlluminationPercent);

  // Direction indicators: Waxing / Waning / Full / New
  const isJodeWaxing = createMemo(() => {
    const p = displayJodePhase();
    return p === 'waxingCrescent' || p === 'firstQuarter' || p === 'waxingGibbous';
  });
  const isJoneWaxing = createMemo(() => {
    const p = displayJonePhase();
    return p === 'waxingCrescent' || p === 'firstQuarter' || p === 'waxingGibbous';
  });

  const jodeArrow = createMemo(() => {
    const p = displayJodePhase();
    if (p === 'full') return '●';
    if (p === 'new') return '○';
    return isJodeWaxing() ? '▲' : '▼';
  });
  const joneArrow = createMemo(() => {
    const p = displayJonePhase();
    if (p === 'full') return '●';
    if (p === 'new') return '○';
    return isJoneWaxing() ? '▲' : '▼';
  });

  // Astronomical SVG Terminator Paths — Isolated via createMemo()
  // Keyed on phase & illumination percentage so it only recomputes when lunar state advances
  const jodeTerminatorPathScene = createMemo(() => {
    const fraction = displayJodeIllumination() / 100;
    return computeMoonTerminator(27, fraction, isJodeWaxing()); // 54px scene disc
  });
  const joneTerminatorPathScene = createMemo(() => {
    const fraction = displayJoneIllumination() / 100;
    return computeMoonTerminator(19, fraction, isJoneWaxing()); // 38px scene disc
  });

  const jodeTerminatorPathLattice = createMemo(() => {
    const fraction = displayJodeIllumination() / 100;
    return computeMoonTerminator(14, fraction, isJodeWaxing()); // 28px lattice disc
  });
  const joneTerminatorPathLattice = createMemo(() => {
    const fraction = displayJoneIllumination() / 100;
    return computeMoonTerminator(10, fraction, isJoneWaxing()); // 20px lattice disc
  });

  const furstockVerdict = createMemo(() => getFurstockLattice(displayJodePhase(), displayJonePhase()));
  const phasePercent = createMemo(() => Math.round(eso().moonFullnessPercent));
  const dayAngle = createMemo(() => (((eso().hour * 60 + eso().minute) / 1440) * 360).toFixed(2));
  const currentComment = createMemo(() => pickComment(eso().timeOfDay, eso().moonPhase, commentSeed()));

  function setRootSetting(key, value) {
    if (!props.widget?.id) return;
    wm.setSettings(props.widget.id, { ...settings(), [key]: value });
  }

  function toggleMoonMode() {
    const next = moonPhaseMode() === 'in-game' ? 'lore' : 'in-game';
    setLegacyMode(next);
    try { localStorage.setItem('redfur-eso-moon-mode', next); } catch {}
    setRootSetting('moonPhaseMode', next);
  }

  onMount(() => {
    // ESO minute ≈ 3 real seconds; update clock every 3s when visible
    const tick = () => {
      if (props.widget?.minimized) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setEso(getESOTime());
      setCommentSeed(Math.floor(Date.now() / 60_000));
    };
    const id = setInterval(tick, 3_000);
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) tick();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    onCleanup(() => {
      clearInterval(id);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    });
  });

  const isRunning = createMemo(() => animateMoons() && !props.widget?.minimized);

  return (
    <div
      class="v2-clock"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      classList={{
        [themeClass()]: !!themeClass(),
        'is-animated': isRunning(),
        'is-still': !isRunning(),
        'is-hovered': hovered(),
      }}
      data-clock-theme={clockTheme() || 'current'}
      style={{
        '--jode-lit': `${displayJodeIllumination()}%`,
        '--jone-lit': `${displayJoneIllumination()}%`,
        '--phase-progress': `${phasePercent()}%`,
        '--day-angle': `${dayAngle()}deg`,
      }}
      role="region"
      aria-label="ESO in-game clock"
    >
      {/* Upper Scene Sky */}
      <div class="v2-clock__scene">
        <div class="v2-clock__stars" aria-hidden="true" />
        {showMoons() && (
          <div class="v2-clock__moonfield">
            {/* Masser (Jode) Sky Orb */}
            <Tooltip
              class="v2-clock__orb-tip v2-clock__orb-tip--jode"
              text={`Masser (Jode — Big Red Moon)\nPhase: ${MOON_LABELS_LOCAL[displayJodePhase()] || eso().moonLabel} (${displayJodeIllumination()}%)\n🐱 ${JODE_TIPS[displayJodePhase()] || ''}`}
            >
              <div class="v2-clock__sky-moon v2-clock__sky-moon--masser" role="img" aria-label={`Masser, ${MOON_LABELS_LOCAL[displayJodePhase()]}`}>
                <svg width="54" height="54" viewBox="0 0 54 54" class="v2-clock__sky-svg">
                  {/* Lit face (Rust-Red base) */}
                  <circle cx="27" cy="27" r="27" fill="#e08a6a" />
                  {/* Terminator Shadow */}
                  {jodeTerminatorPathScene() && (
                    <path d={jodeTerminatorPathScene()} fill="#2a1a18" />
                  )}
                  {/* Brass Ring Signature */}
                  <circle cx="27" cy="27" r="26.5" fill="none" stroke="#d4a24c" stroke-width="1" opacity="0.85" />
                </svg>
                <span class="v2-clock__sky-label">Masser</span>
              </div>
            </Tooltip>

            {/* Secunda (Jone) Sky Orb */}
            <Tooltip
              class="v2-clock__orb-tip v2-clock__orb-tip--jone"
              text={`Secunda (Jone — Little Silver Moon)\nPhase: ${MOON_LABELS_LOCAL[displayJonePhase()] || ''} (${displayJoneIllumination()}%)\n🐱 ${JONE_TIPS[displayJonePhase()] || ''}`}
            >
              <div class="v2-clock__sky-moon v2-clock__sky-moon--secunda" role="img" aria-label={`Secunda, ${MOON_LABELS_LOCAL[displayJonePhase()]}`}>
                <svg width="38" height="38" viewBox="0 0 38 38" class="v2-clock__sky-svg">
                  {/* Lit face (Ivory moonlight) */}
                  <circle cx="19" cy="19" r="19" fill="#f1ede4" />
                  {/* Terminator Shadow */}
                  {joneTerminatorPathScene() && (
                    <path d={joneTerminatorPathScene()} fill="#24252e" />
                  )}
                  {/* Brass Ring Signature */}
                  <circle cx="19" cy="19" r="18.5" fill="none" stroke="#d4a24c" stroke-width="1" opacity="0.85" />
                </svg>
                <span class="v2-clock__sky-label">Secunda</span>
              </div>
            </Tooltip>
          </div>
        )}
        <div class="v2-clock__horizon" aria-hidden="true" />
        <div class="v2-clock__dial" aria-hidden="true">
          <span class="v2-clock__dial-ring" />
          <span class="v2-clock__dial-hand" />
        </div>
      </div>

      {/* Main Time Readout */}
      <div class="v2-clock__readout">
        <div class="v2-clock__time">{eso().timeString}</div>
        <div class="v2-clock__tod">{TIME_OF_DAY_LABELS[eso().timeOfDay] || 'Night'} in Tamriel</div>
        {showDate() && <div class="v2-clock__date">{eso().dateString}</div>}
      </div>

      {/* Ja-Kha'jay Celestial Lattice: The Three-Layer Display */}
      {showPhase() && (
        <div class="v2-clock__lattice" role="region" aria-label="Khajiit Celestial Lattice (Ja-Kha'jay)">
          {/* Layer 1 & 2 & 3: Masser */}
          <div class="v2-clock__moon-row v2-clock__moon-row--masser">
            <div class="v2-clock__disc-box">
              <svg width="28" height="28" viewBox="0 0 28 28" class="v2-clock__disc-svg" aria-hidden="true">
                <circle cx="14" cy="14" r="14" fill="#e08a6a" />
                {jodeTerminatorPathLattice() && (
                  <path d={jodeTerminatorPathLattice()} fill="#2a1a18" />
                )}
                <circle cx="14" cy="14" r="13.5" fill="none" stroke="#d4a24c" stroke-width="1" opacity="0.9" />
              </svg>
            </div>
            <div class="v2-clock__moon-info">
              <div class="v2-clock__moon-head">
                <span class="v2-clock__moon-badge">MASSER</span>
                <span class="v2-clock__moon-lore">Jode · Big Moon</span>
              </div>
              <div class="v2-clock__moon-phase-text">
                {MOON_LABELS_LOCAL[displayJodePhase()] || displayJodePhase()}
              </div>
            </div>
            <div class="v2-clock__moon-metrics">
              <span class="v2-clock__moon-arrow" classList={{ 'is-wax': isJodeWaxing(), 'is-wan': !isJodeWaxing() }}>
                {jodeArrow()}
              </span>
              <span class="v2-clock__moon-pct">{displayJodeIllumination()}%</span>
            </div>
          </div>

          {/* Layer 1 & 2 & 3: Secunda */}
          <div class="v2-clock__moon-row v2-clock__moon-row--secunda">
            <div class="v2-clock__disc-box">
              <svg width="20" height="20" viewBox="0 0 20 20" class="v2-clock__disc-svg" aria-hidden="true">
                <circle cx="10" cy="10" r="10" fill="#f1ede4" />
                {joneTerminatorPathLattice() && (
                  <path d={joneTerminatorPathLattice()} fill="#24252e" />
                )}
                <circle cx="10" cy="10" r="9.5" fill="none" stroke="#d4a24c" stroke-width="1" opacity="0.9" />
              </svg>
            </div>
            <div class="v2-clock__moon-info">
              <div class="v2-clock__moon-head">
                <span class="v2-clock__moon-badge">SECUNDA</span>
                <span class="v2-clock__moon-lore">Jone · Little Moon</span>
              </div>
              <div class="v2-clock__moon-phase-text">
                {MOON_LABELS_LOCAL[displayJonePhase()] || displayJonePhase()}
              </div>
            </div>
            <div class="v2-clock__moon-metrics">
              <span class="v2-clock__moon-arrow" classList={{ 'is-wax': isJoneWaxing(), 'is-wan': !isJoneWaxing() }}>
                {joneArrow()}
              </span>
              <span class="v2-clock__moon-pct">{displayJoneIllumination()}%</span>
            </div>
          </div>

          {/* Canonical Khajiit Furstock Line */}
          <div class="v2-clock__lattice-furstock">
            <div class="v2-clock__lattice-divider" />
            <div class="v2-clock__furstock-text">
              ✨ {furstockVerdict()}
            </div>
          </div>

          {/* Cycle Bar & Next Phase Timer */}
          <div class="v2-clock__cycle-row">
            <span>Next Phase Window</span>
            <strong>{eso().hoursUntilNextPhase.toFixed(1)} real hrs</strong>
          </div>
        </div>
      )}

      {/* Auxiliary Chips & Mode Toggle */}
      <div class="v2-clock__chips">
        {showEarthMoon() && (
          <Tooltip text="What your real-world local sky is doing right now">
            <div class="v2-clock__real-moon">
              <span class="v2-clock__real-moon-glyph">{eso().realMoonGlyph}</span>
              <span class="v2-clock__real-moon-label">Earth: {eso().realMoonLabel}</span>
            </div>
          </Tooltip>
        )}
        {showLoreToggle() && (
          <button
            type="button"
            class="v2-clock__mode-pill"
            classList={{ 'is-lore': moonPhaseMode() === 'lore' }}
            title={moonPhaseMode() === 'in-game'
              ? 'Showing in-game phases (Jone = Jode). Click for lore-accurate independent phases.'
              : 'Showing lore phases. Click for in-game view.'}
            onClick={toggleMoonMode}
          >
            {moonPhaseMode() === 'in-game' ? 'In-game moons' : 'Lore moons'}
          </button>
        )}
      </div>

      {/* Fissal Planetary Commentary */}
      {showCommentary() && (
        <div class="v2-clock__omen">
          <span class="v2-clock__omen-kicker">Fissal</span>
          <span class="v2-clock__omen-copy">{currentComment()}</span>
          <span class="v2-clock__omen-era">
            {eso().moonWaxing ? 'Full moon in' : 'New moon in'} {Math.abs(eso().hoursUntilFull).toFixed(1)} hrs · {eso().era}E {eso().year}
          </span>
        </div>
      )}
    </div>
  );
}
